import { Text, Weight } from 'components/typography'
import { AnalyticsArea, useAnalyticsEventTracker } from 'hooks/analytics-tracker'
import React, { Fragment } from 'react'
import {
  NestedMenuItem,
  NestedMenuTextWrapper,
  NestedProductIcon,
  NestedSvgIcon,
  PortfolioLine,
  StyledSubtitle,
  StyledTitle,
} from '../../styling/desktop'
import {
  MenuText,
  MenuTitle,
  MenuTitleContainer,
  MobileMenuItem,
  OpenMenuContainerMain,
} from '../../styling/mobile'
import { getProcessedValue } from '../desktop/utils'
import { NestedNavListProps, useActiveLinking } from '../use-active-linking'

import ArrowIcon from 'assets/menu-icons/menu-arrow.svg?react'
import Big from 'big.js'
import { useTourGuide } from 'context/tour-guide/use-tour-guide'
import { useNavigate } from 'react-router-dom'
import ThemeToggle from '../../menus/social-media'
import { PortfolioReturns } from '../storage/interface'
import { useAccount } from 'wagmi'

export type MenuProps = {
  portfolioReturns: PortfolioReturns
  closeMenu: () => void
  selectedItem?: NestedNavListProps
  setSelectedItem: React.Dispatch<React.SetStateAction<NestedNavListProps | undefined>>
}

/**
 * renders the main menu
 * @returns
 */
export const MobileNavigation: React.FC<MenuProps> = ({
  portfolioReturns,
  closeMenu,
  selectedItem,
  setSelectedItem,
}) => {
  const { navigationEvent } = useAnalyticsEventTracker(AnalyticsArea.NavigationBar)
  const navigate = useNavigate()
  const { navigationList, nestedNavigationList } = useActiveLinking()
  const [list, setList] = React.useState<NestedNavListProps[]>()
  const { address } = useAccount()
  const { currentStep, run, menuOpen, setCurrentStep, setMenuOpen } = useTourGuide()

  const handleNavigate = (url: string, external?: boolean) => {
    if (menuOpen === 'OPEN' && currentStep === 12) {
      setMenuOpen('CLOSED')
      setCurrentStep(13)
    }

    navigationEvent(url, external)
    !external && navigate(url)
    setSelectedItem(undefined)
    closeMenu()
  }

  const pickNestedMenu = (item: string) => {
    list && setSelectedItem(list.filter(({ title }) => title.toLowerCase() === item)[0])
  }

  React.useEffect(() => {
    const runningOrPaused = run || currentStep > 0
    if (runningOrPaused && menuOpen === 'OPEN' && currentStep === 11) {
      setSelectedItem(list?.find(({ isPortfolio }) => isPortfolio === true))
    } else {
      closeMenu()
    }

    if (menuOpen === 'CLOSED') {
      closeMenu()
    }
  }, [menuOpen, currentStep])

  React.useEffect(() => {
    setList(
      nestedNavigationList(
        (portfolioReturns.investmentValue ?? Big(0)).precision(2).toString(),
        getProcessedValue(portfolioReturns).toString(),
      ),
    )
    selectedItem && pickNestedMenu(selectedItem.title)
  }, [address, portfolioReturns])

  return (
    <>
      <OpenMenuContainerMain>
        <MenuTitleContainer>
          <MenuTitle>
            <MenuText
              onClick={() => setSelectedItem(undefined)}
              isSelected={selectedItem !== undefined}
            >
              {`Menu ${selectedItem ? '>' : ''}`}
            </MenuText>
            <Text style={{ marginLeft: '5px' }} type="two" weight={Weight.Bold}>
              {selectedItem ? selectedItem.title : ''}
            </Text>
          </MenuTitle>
          {selectedItem === undefined && <ThemeToggle />}
        </MenuTitleContainer>
        {selectedItem === undefined
          ? navigationList.map((path) => (
              <MobileMenuItem key={path} onClick={() => pickNestedMenu(path)}>
                <Text type="two" weight={Weight.Medium}>
                  {path}
                </Text>
                <ArrowIcon />
              </MobileMenuItem>
            ))
          : selectedItem.data.map((props, index) => (
              <Fragment key={`${props.name}-${props.description}`}>
                <NestedMenuItem
                  id={`${props.name.trim().replace(' ', '-').toLowerCase()}`}
                  highlightable={
                    !selectedItem.isPortfolio || (selectedItem.isPortfolio && index <= 1)
                  }
                  isCompany={selectedItem.title === 'Company'}
                  isPortfolio={
                    currentStep === 11 ||
                    currentStep === 12 ||
                    (selectedItem.isPortfolio && index <= 1)
                  }
                  onClick={() =>
                    props.link &&
                    handleNavigate(
                      props.link,
                      props.name === 'Documentation' || props.name === 'Blog',
                    )
                  }
                >
                  {selectedItem.title === 'Products' && typeof props.icon === 'string' ? (
                    <NestedProductIcon src={props.icon} />
                  ) : (
                    <NestedSvgIcon
                      icon={props.icon as React.FunctionComponent<React.SVGProps<SVGSVGElement>>}
                    />
                  )}
                  <NestedMenuTextWrapper>
                    <StyledTitle isPositive={props.colored && Number(props.name) >= 0}>
                      {!props.colored
                        ? props.name
                        : `${Number(props.name) >= 0 ? '+' : ''}${props.name}%`}
                    </StyledTitle>
                    <StyledSubtitle>{props.description}</StyledSubtitle>
                  </NestedMenuTextWrapper>
                </NestedMenuItem>
                {selectedItem.isPortfolio && index === 1 && <PortfolioLine />}
              </Fragment>
            ))}
      </OpenMenuContainerMain>
    </>
  )
}
